import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Send } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } },
};

export default function BespokeForm() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", message: "", travel_dates: "", num_guests: null, budget: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        lead_type: "custom_tour",
        ...formData,
        num_guests: formData.num_guests ? parseInt(formData.num_guests) : null,
      });
      setSuccess(true);
    } catch (error) {
      setSubmitting(false);
    }
    setSubmitting(false);
  };

  const update = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  return (
    <section id="bespoke" data-testid="bespoke-form" className="py-24 md:py-32 lg:py-40 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left text */}
          <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
            <motion.p variants={fadeUp} className="font-sans text-[#C5A059] text-[10px] md:text-xs tracking-[0.25em] uppercase mb-4">
              Build Your Own
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-white text-4xl md:text-5xl font-light tracking-tight mb-6">
              Bespoke
              <br />
              <span className="italic">Experience</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="font-sans text-white/60 text-sm md:text-base font-light leading-relaxed mb-8">
              Can't find exactly what you're looking for? We specialize in designing one-of-a-kind
              adventures tailored to your interests, timeline, and group size. From corporate retreats
              to family celebrations, your vision becomes our blueprint.
            </motion.p>
            <motion.div variants={fadeUp} className="space-y-3">
              {["Helicopter tours over volcanic craters", "Offroad experiences through cloud forests", "Private surf lessons with champions", "Cultural immersion with local artisans", "Multi-day expeditions across El Salvador & Guatemala"].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-1 h-1 bg-[#C5A059] rounded-full" />
                  <span className="font-sans text-white/50 text-sm">{item}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right form */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.3, duration: 0.8 }}>
            {success ? (
              <div className="border border-white/10 p-12 text-center" data-testid="bespoke-success">
                <div className="w-12 h-12 border border-[#C5A059] flex items-center justify-center mx-auto mb-6">
                  <Send size={18} className="text-[#C5A059]" />
                </div>
                <h3 className="font-serif text-white text-2xl mb-3">Message Received</h3>
                <p className="font-sans text-white/60 text-sm">Our concierge team will reach out within 24 hours to begin crafting your experience.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="border border-white/10 p-8 md:p-10 space-y-6" data-testid="bespoke-form-fields">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <input type="text" placeholder="Full Name" required value={formData.name}
                    onChange={e => update("name", e.target.value)}
                    className="bg-transparent border-b border-white/15 focus:border-[#C5A059] text-white font-sans text-sm py-3 outline-none transition-colors placeholder:text-white/20"
                    data-testid="bespoke-name-input" />
                  <input type="email" placeholder="Email" required value={formData.email}
                    onChange={e => update("email", e.target.value)}
                    className="bg-transparent border-b border-white/15 focus:border-[#C5A059] text-white font-sans text-sm py-3 outline-none transition-colors placeholder:text-white/20"
                    data-testid="bespoke-email-input" />
                  <input type="tel" placeholder="Phone" required value={formData.phone}
                    onChange={e => update("phone", e.target.value)}
                    className="bg-transparent border-b border-white/15 focus:border-[#C5A059] text-white font-sans text-sm py-3 outline-none transition-colors placeholder:text-white/20"
                    data-testid="bespoke-phone-input" />
                  <input type="text" placeholder="Preferred Travel Dates" value={formData.travel_dates}
                    onChange={e => update("travel_dates", e.target.value)}
                    className="bg-transparent border-b border-white/15 focus:border-[#C5A059] text-white font-sans text-sm py-3 outline-none transition-colors placeholder:text-white/20"
                    data-testid="bespoke-dates-input" />
                  <input type="number" placeholder="Number of Guests" min={1} max={50} value={formData.num_guests || ""}
                    onChange={e => update("num_guests", e.target.value)}
                    className="bg-transparent border-b border-white/15 focus:border-[#C5A059] text-white font-sans text-sm py-3 outline-none transition-colors placeholder:text-white/20"
                    data-testid="bespoke-guests-input" />
                  <select value={formData.budget} onChange={e => update("budget", e.target.value)}
                    className="bg-transparent border-b border-white/15 focus:border-[#C5A059] text-white font-sans text-sm py-3 outline-none transition-colors"
                    data-testid="bespoke-budget-select">
                    <option value="" className="bg-[#0A0A0A]" disabled>Budget Range</option>
                    <option value="$500-$1000" className="bg-[#0A0A0A]">$500 - $1,000</option>
                    <option value="$1000-$2500" className="bg-[#0A0A0A]">$1,000 - $2,500</option>
                    <option value="$2500-$5000" className="bg-[#0A0A0A]">$2,500 - $5,000</option>
                    <option value="$5000+" className="bg-[#0A0A0A]">$5,000+</option>
                  </select>
                </div>
                <textarea placeholder="Describe your dream experience..." rows={4} required value={formData.message}
                  onChange={e => update("message", e.target.value)}
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#C5A059] text-white font-sans text-sm py-3 outline-none transition-colors resize-none placeholder:text-white/20"
                  data-testid="bespoke-message-input" />
                <button type="submit" disabled={submitting} data-testid="bespoke-submit-btn"
                  className="w-full border border-[#C5A059] text-[#C5A059] font-sans text-xs tracking-[0.2em] uppercase py-4 hover:bg-[#C5A059] hover:text-black transition-all duration-500 disabled:opacity-30">
                  {submitting ? "Sending..." : "Send Inquiry"}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
